import { useEditorBlocksContext } from "@pathwright/blocks-engine"
import DropdownTrigger from "@pathwright/ui/src/components/dropdown/DropdownTrigger"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import Tooltip from "@pathwright/ui/src/components/tooltip/Tooltip"
import View from "@pathwright/ui/src/components/ui/View"
import React from "react"
import styled from "styled-components"
import BlockLayoutPreview from "./BlockLayoutPreview"
import { getBlockTypeLabel, getBlockTypeLayoutOptions } from "./utils"

const LayoutSwitcherContainer = styled(View.Primary)`
  position: relative;
  display: flex;
  padding: 10px;
  top: 5px;
  left: -5px;
  border-radius: 8px;
  box-shadow: 0px 0px 30px rgba(0, 0, 0, 0.18);
`
const LayoutSwitcherPreviewContainer = styled.div`
  padding: 10px;
`

const LayoutMenu = styled(View.Primary)`
  display: flex;
  width: 100%;
  flex-direction: column;
`

const LayoutItem = styled.div`
  padding: 5px 10px;
  width: 100%;
  white-space: nowrap;
  cursor: pointer;
  color: ${(p) => (p.selected ? "#2A2E33" : "#666")};
  border-radius: 12px;
  &:hover {
    background-color: #efefef;
  }
`

const DropdownButton = styled.div`
  cursor: pointer;

  @media only screen and (max-width: 400px) {
    font-size: 0.9em;
  }

  @media only screen and (max-width: 350px) {
    font-size: 0.7em;
  }
`

const LayoutLabel = styled.span`
  display: flex;
  align-items: center;
  white-space: pre;
`

const propsAreEqual = (prevProps, nextProps) => {
  if (prevProps.block !== nextProps.block) return false

  return true
}

const BlockLayoutSwitcher = React.memo((props) => {
  const { onSelectLayout, onOpenMenu, onCloseMenu, block, preventTooltip } =
    props

  const { layout, type, data } = block

  const { blockTypes } = useEditorBlocksContext()

  const [selectLayoutIndex, setSelectedLayoutIndex] = React.useState(null)
  const options = React.useMemo(() => {
    if (type && blockTypes) {
      return getBlockTypeLayoutOptions({ type, blockTypes })
    }
    return []
  }, [type, blockTypes, layout])

  React.useEffect(() => {
    const selectLayoutIndex = options.findIndex((op) => op.key === layout)
    setSelectedLayoutIndex(selectLayoutIndex)
  }, [layout])

  const handleSelectLayout = (option) => () => {
    onSelectLayout(option.key)
  }

  const renderLayoutPreview = () => {
    if (!options.length) return null

    const selectedLayout = options[selectLayoutIndex] || {
      key: "",
      data: {}
    }

    const passProps = {
      ...block,
      layout: selectedLayout.key,
      data: { ...selectedLayout.data, ...data }
    }

    return <BlockLayoutPreview {...passProps} />
  }

  return (
    <DropdownTrigger
      alignment="left"
      closeOnOverlayClick={true}
      onShow={onOpenMenu}
      onHide={onCloseMenu}
      renderDropdown={() => {
        return (
          <LayoutSwitcherContainer>
            <LayoutMenu>
              {options.map((option, i) => {
                const selected = selectLayoutIndex === i
                return (
                  <LayoutItem
                    key={i}
                    selected={selected}
                    onMouseEnter={() => setSelectedLayoutIndex(i)}
                    onClick={handleSelectLayout(option)}
                  >
                    {option.label}
                  </LayoutItem>
                )
              })}
            </LayoutMenu>
            <LayoutSwitcherPreviewContainer
              onClick={handleSelectLayout(options[selectLayoutIndex])}
            >
              {renderLayoutPreview()}
            </LayoutSwitcherPreviewContainer>
          </LayoutSwitcherContainer>
        )
      }}
    >
      <DropdownButton>
        <Tooltip title={!preventTooltip && "Choose a layout for this block"}>
          <LayoutLabel>
            {getBlockTypeLabel({ type, blockTypes, layout })}
            <span> </span>
            <Pathicon icon="chevron-down" />
          </LayoutLabel>
        </Tooltip>
      </DropdownButton>
    </DropdownTrigger>
  )
}, propsAreEqual)
BlockLayoutSwitcher.displayName = "BlockLayoutSwitcher"

export default BlockLayoutSwitcher
